import { useBenefitBackupPreview } from "@/features/benefit-workspace/useBenefitChildReads"
export type { PseBackupCostPreview } from "@/features/benefit-workspace/benefitChildContracts"

type Props = {
  benefitId: string
  applicationDate: string
  programYear: number | null | undefined
  energyType: number | string | null | undefined
  usedBackup: boolean
  recordedGasUsage: number | null | undefined
  recordedElectricUsage: number | null | undefined
  recordedTotalUsage: number | null | undefined
  formatMoney: (value: number | null | undefined) => string
}

function Field({ label, value }: { label: string; value: string }) {
  return <div className="flex justify-between py-1.5" data-testid={`pse-detail-${label.toLowerCase().replaceAll(" ", "-")}`}>
    <span className="text-muted-foreground text-sm">{label}</span>
    <span className="text-sm font-medium">{value}</span>
  </div>
}

/**
 * Read-only PSE HELP backup settings shown beside, but never substituted for, recorded usage.
 * The request is keyed by the persisted target year and energy type so a stale response cannot
 * label a different certified file as the current setting.
 */
export function PseBackupCostDetails({
  benefitId,
  applicationDate,
  programYear,
  energyType,
  usedBackup,
  recordedGasUsage,
  recordedElectricUsage,
  recordedTotalUsage,
  formatMoney,
}: Props) {
  const { preview, loading, error } = useBenefitBackupPreview(benefitId, applicationDate, programYear, energyType, usedBackup)

  const cost = (fuel: number) => preview?.costs.find(item => item.fuel === fuel)?.amount
  const display = (value: number | undefined) => value == null ? "—" : formatMoney(value)

  return <div data-testid="pse-backup-details" className="space-y-3">
    <div data-testid="pse-recorded-usage">
      <Field label="Recorded Gas Usage" value={formatMoney(recordedGasUsage)} />
      <Field label="Recorded Electric Usage" value={formatMoney(recordedElectricUsage)} />
      <Field label="Recorded Total Usage" value={formatMoney(recordedTotalUsage)} />
    </div>
    {usedBackup && <div className="rounded-md border bg-muted/30 p-3 text-sm" data-testid="pse-configured-backup-costs">
      <p className="font-medium">PSE standard backup costs (published settings)</p>
      <p className="text-muted-foreground">PY {programYear ?? "—"} · configured settings only; not recorded usage, historical amounts used, an adjusted calculation, or a final benefit.</p>
      {loading ? <p role="status" className="mt-2 text-muted-foreground">Loading canonical backup settings…</p>
        : error ? <p role="alert" className="mt-2 text-destructive">{error}</p>
          : !preview || preview.status !== "configured" ? <p className="mt-2 text-muted-foreground">Backup settings are unavailable for this program year and energy type.</p>
            : <div className="mt-2">
              <Field label="Configured Backup — Gas (PSE)" value={display(cost(11))} />
              <Field label="Configured Backup — Electric (PSE)" value={display(cost(10))} />
              <Field label="Configured Backup — Total (PSE)" value={formatMoney(preview.totalConfiguredCost)} />
            </div>}
    </div>}
  </div>
}
